import { Container } from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SearchBoxPersisted from "components/SearchBoxPersisted";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  ILicensureBodyListItem,
  LicensureBodyAdminFilter,
  ListRequestOfLicensureBodyAdminFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";

const LicensureBodyList = () => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const { licensureBodyService, licensureBodyDetailsUrl } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { organizationId } = contextStore.useState();
  const { licensureBodySearchTerm } = persistanceStore.useState();

  useListTitle("licensureBodies");

  useEffect(() => {
    setPage(0);
  }, [organizationId]);

  const listRequest = ListRequestOfLicensureBodyAdminFilter.fromJS({
    pageNumber: page,
    pageSize: rowsPerPage,
    sortField: orderBy,
    sortDescending: order === "desc",
    parameters: LicensureBodyAdminFilter.fromJS({
      searchText: licensureBodySearchTerm || undefined,
    }),
  });

  const { data, isFetching } = useQuery(
    ["licensureBodyService.getLicensureBodies", organizationId, listRequest],
    () => licensureBodyService.getLicensureBodies(listRequest),
    {
      keepPreviousData: true,
    },
  );

  const licensureBodies = ((data?.data as ILicensureBodyListItem[]) ?? []).map(
    (lb) => ({
      ...lb,
      id: lb.id!,
    }),
  );

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "acronym",
      type: "text",
      label: t({ id: "acronymLabel" }),
    },
    {
      accessor: "submissionAuthorityName",
      noSort: true,
      type: "text",
      label: t({ id: "submissionAuthority" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(licensureBodyDetailsUrl(id));
  };

  const handleSetPersistedSearchTerm = (searchText: string): void => {
    persistanceStore.update((s) => {
      s.licensureBodySearchTerm = searchText;
    });
  };

  return (
    <Container>
      <SearchBoxPersisted
        setPersistedSearchTerm={handleSetPersistedSearchTerm}
        persistedSearchTerm={licensureBodySearchTerm}
      />

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: licensureBodies,
          loading: isFetching,
          dense: true,
          title: t({ id: "licensureBodies" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) =>
              licensureBodyDetailsUrl(id),
            ),
          ],
        }}
      />
    </Container>
  );
};

export default LicensureBodyList;
